import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { t, Trans } from '@lingui/macro';
import { CircularProgress, IconButton } from '@mui/material';
import { CaretLeft, CaretRight, FilePdf } from '@phosphor-icons/react';
import type { PDFDocumentProxy } from 'pdfjs-dist';
import { Text } from 'components/text/Text';
import { colors } from 'styles/theme/colors';
import {
  OfWord,
  PageNavFraction,
  PageNumberField,
  PdfCanvasClip,
  PdfViewerCentered,
  PdfViewerRoot,
  PreviewCanvas,
  PreviewToolbarFloating,
  TotalPagesLabel,
} from './pdfViewer.styles';

type Pdfjs = typeof import('pdfjs-dist');

/**
 * pdfjs-dist is heavy (~1 MB) — load it (and its worker) on first render of a
 * viewer instead of on app start. The promise is cached module-wide so repeated
 * viewers (or re-opens) pay the import exactly once.
 */
let pdfjsLoader: Promise<Pdfjs> | null = null;
const loadPdfjs = (): Promise<Pdfjs> => {
  pdfjsLoader ??= Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]).then(([pdfjs, worker]) => {
    pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
    return pdfjs;
  });
  return pdfjsLoader;
};

export type PdfPagerInfo = { currentPage: number; totalPages: number };

export type PdfViewerProps = {
  /** PDF bytes — a `Blob` (including `File`), `ArrayBuffer`, or `Uint8Array`. */
  source: Blob | ArrayBuffer | Uint8Array;
  /** Accessible name for the rendered page canvas (e.g. the document file name). */
  ariaLabel?: string;
  /** Reports the pager position upward whenever it changes. */
  onPagerInfo?: (info: PdfPagerInfo) => void;
  /** Hide the floating pager toolbar (single-page embeds). */
  hideToolbar?: boolean;
  /**
   * Fired when the document fails to load/parse (the viewer shows its own inline
   * error state either way) — lets a host swap in a fallback surface instead.
   */
  onLoadError?: () => void;
};

/** Copy the caller's bytes — pdfjs transfers (and detaches) the buffer it is given. */
const toBytes = async (source: PdfViewerProps['source']): Promise<Uint8Array> => {
  if (source instanceof Blob) {
    if (typeof source.arrayBuffer === 'function') {
      return new Uint8Array(await source.arrayBuffer());
    }
    // jsdom's Blob may lack `.arrayBuffer()` — go through FileReader.
    return new Promise<Uint8Array>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const bytes = reader.result;
        if (bytes instanceof ArrayBuffer) {
          resolve(new Uint8Array(bytes));
        } else {
          reject(new Error('Unexpected FileReader result'));
        }
      };
      reader.onerror = () => reject(reader.error);
      reader.readAsArrayBuffer(source);
    });
  }
  if (source instanceof Uint8Array) {
    return new Uint8Array(source);
  }
  return new Uint8Array(source.slice(0));
};

/**
 * Shared canvas PDF viewer (promoted from `features/files/FilePreview`, the first
 * render site — the compliance document/preview modals are the 2nd+3rd consumers).
 * Loads pdfjs lazily, renders the current page width-fitted with DPR scaling, and
 * offers a floating pager toolbar. Own loading / error states; the host supplies
 * the box (the root fills and scrolls inside it).
 */
export const PdfViewer = ({
  source,
  ariaLabel,
  onPagerInfo,
  hideToolbar,
  onLoadError,
}: PdfViewerProps) => {
  const [phase, setPhase] = useState<'loading' | 'error' | 'ready'>('loading');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const pdfRef = useRef<PDFDocumentProxy | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const onPagerInfoRef = useRef(onPagerInfo);
  onPagerInfoRef.current = onPagerInfo;
  const onLoadErrorRef = useRef(onLoadError);
  onLoadErrorRef.current = onLoadError;

  // Track the host panel's width so the page scales to fit (the canvas is bitmap-sized).
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const updateSize = () => setContainerWidth(el.clientWidth);

    updateSize();
    let raf1 = 0;
    let raf2 = 0;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(updateSize);
    });

    const ro = new ResizeObserver(updateSize);
    ro.observe(el);
    window.addEventListener('resize', updateSize);

    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      ro.disconnect();
      window.removeEventListener('resize', updateSize);
    };
  }, [phase]);

  // Load pdfjs + parse the document; race-guarded against a source swap / unmount.
  useEffect(() => {
    let cancelled = false;
    setPhase('loading');
    setPage(1);
    setTotalPages(null);

    void (async () => {
      try {
        const pdfjs = await loadPdfjs();
        const data = await toBytes(source);
        if (cancelled) return;
        void pdfRef.current?.destroy();
        pdfRef.current = null;
        const pdf = await pdfjs.getDocument({ data }).promise;
        if (cancelled) {
          await pdf.destroy();
          return;
        }
        pdfRef.current = pdf;
        setTotalPages(pdf.numPages);
        setPhase('ready');
      } catch {
        if (!cancelled) {
          setPhase('error');
          onLoadErrorRef.current?.();
        }
      }
    })();

    return () => {
      cancelled = true;
      void pdfRef.current?.destroy();
      pdfRef.current = null;
    };
  }, [source]);

  useEffect(() => {
    if (totalPages != null && page > totalPages) setPage(totalPages);
  }, [totalPages, page]);

  useEffect(() => {
    if (phase !== 'ready' || totalPages == null) return;
    onPagerInfoRef.current?.({ currentPage: page, totalPages });
  }, [phase, page, totalPages]);

  // Render the current page to the canvas — progressively: page 1 paints as soon as
  // the document resolves, later pages on demand. Cancels a stale render mid-flight.
  useEffect(() => {
    const pdf = pdfRef.current;
    const canvas = canvasRef.current;
    if (!pdf || !canvas || phase !== 'ready' || totalPages == null || containerWidth <= 0) {
      return undefined;
    }

    const maxW = Math.max(1, containerWidth);
    let cancelled = false;
    const renderTaskRef: { current: { cancel: () => void; promise: Promise<void> } | null } = {
      current: null,
    };

    void (async () => {
      try {
        const pdfPage = await pdf.getPage(Math.min(Math.max(1, page), pdf.numPages));
        if (cancelled) return;

        const base = pdfPage.getViewport({ scale: 1 });
        const scale = maxW / base.width;
        const viewport = pdfPage.getViewport({ scale });
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        const dpr = window.devicePixelRatio || 1;
        const w = Math.floor(viewport.width);
        const h = Math.floor(viewport.height);
        canvas.width = Math.floor(w * dpr);
        canvas.height = Math.floor(h * dpr);
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const task = pdfPage.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined,
        });
        renderTaskRef.current = task;
        await task.promise;
        if (!cancelled) renderTaskRef.current = null;
      } catch {
        /* cancelled */
      }
    })();

    return () => {
      cancelled = true;
      renderTaskRef.current?.cancel();
      renderTaskRef.current = null;
    };
  }, [page, phase, totalPages, containerWidth, source]);

  const [pageFieldDraft, setPageFieldDraft] = useState<string | null>(null);
  useEffect(() => setPageFieldDraft(null), [source]);

  if (phase === 'loading') {
    return (
      <PdfViewerRoot>
        <PdfViewerCentered data-testid="pdf_viewer_loading">
          <CircularProgress aria-label={t`Loading PDF`} />
        </PdfViewerCentered>
      </PdfViewerRoot>
    );
  }

  if (phase === 'error') {
    return (
      <PdfViewerRoot>
        <PdfViewerCentered data-testid="pdf_viewer_error">
          <FilePdf size={48} weight="duotone" color={colors.brand[600]} />
          <Text variant="bodySm" color="secondary" top={8} align="center">
            <Trans>The PDF could not be displayed.</Trans>
          </Text>
        </PdfViewerCentered>
      </PdfViewerRoot>
    );
  }

  const disabledPrev = page <= 1;
  const disabledNext = totalPages != null && page >= totalPages;
  const pageFieldDisplay = pageFieldDraft ?? String(page);

  const applyPageFromInput = (raw: string) => {
    const digits = raw.replace(/\D/g, '');
    const n = parseInt(digits, 10);
    if (Number.isNaN(n) || n < 1) return;
    const max = totalPages ?? 9999;
    setPage(Math.min(max, Math.max(1, n)));
  };

  return (
    <PdfViewerRoot>
      <PdfCanvasClip ref={containerRef}>
        <PreviewCanvas ref={canvasRef} data-pdf-page={page} aria-label={ariaLabel} />
      </PdfCanvasClip>
      {!hideToolbar && (
        <PreviewToolbarFloating>
          <IconButton
            size="sm"
            aria-label={t`Previous page`}
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={disabledPrev}
            sx={{
              color: disabledPrev ? colors.grey[600] : colors.grey[0],
              '&.Mui-disabled': { opacity: 1 },
            }}
          >
            <CaretLeft
              size={18}
              weight="bold"
              color={disabledPrev ? colors.grey[600] : colors.grey[0]}
            />
          </IconButton>
          <PageNavFraction
            aria-label={
              totalPages != null
                ? t`Page ${page} of ${totalPages}`
                : t`Page ${page}, total not shown`
            }
          >
            <PageNumberField
              type="text"
              inputMode="numeric"
              aria-label={t`Current page`}
              value={pageFieldDisplay}
              onFocus={() => setPageFieldDraft(String(page))}
              onChange={e => setPageFieldDraft(e.target.value)}
              onBlur={e => {
                applyPageFromInput(e.target.value);
                setPageFieldDraft(null);
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') e.currentTarget.blur();
              }}
            />
            <OfWord>{t`of`}</OfWord>
            <TotalPagesLabel variant="bodySm">
              {totalPages != null ? String(totalPages) : '…'}
            </TotalPagesLabel>
          </PageNavFraction>
          <IconButton
            size="sm"
            aria-label={t`Next page`}
            onClick={() => setPage(p => (totalPages != null ? Math.min(totalPages, p + 1) : p + 1))}
            disabled={disabledNext}
            sx={{
              color: disabledNext ? colors.grey[600] : colors.grey[0],
              '&.Mui-disabled': { opacity: 1 },
            }}
          >
            <CaretRight
              size={18}
              weight="bold"
              color={disabledNext ? colors.grey[600] : colors.grey[0]}
            />
          </IconButton>
        </PreviewToolbarFloating>
      )}
    </PdfViewerRoot>
  );
};
